/**
 * Article Product Slider
 * Provides drag-to-scroll functionality and navigation controls
 */

class ArticleProductSlider {
  constructor(element) {
    this.slider = element;
    this.track = this.slider.querySelector('[data-slider-track]');
    this.wrapper = this.slider.querySelector('[data-slider-wrapper]');
    this.slides = this.slider.querySelectorAll('[data-slider-slide]');
    this.prevBtn = this.slider.querySelector('[data-slider-prev]');
    this.nextBtn = this.slider.querySelector('[data-slider-next]');
    
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.translateX = 0;
    this.slideWidth = 0;
    this.visibleSlides = 1;
    this.maxIndex = 0;
    
    // Touch detection properties
    this.touchStartTime = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isTouch = false;
    this.hasMoved = false;
    this.dragDelay = null;
    this.isDragDelayed = false;
    
    this.init();
  }
  
  init() {
    if (!this.track || !this.slides.length) {
      console.warn('ArticleProductSlider: Missing required elements');
      return;
    }
    
    // Ensure DOM is fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeSlider());
    } else {
      this.initializeSlider();
    }
  }
  
  initializeSlider() {
    this.calculateDimensions();
    this.setupEventListeners();
    this.updateNavigation();
    this.setupResizeObserver();
  }
  
  calculateDimensions() {
    const wrapperWidth = this.wrapper.offsetWidth;
    const slideWidth = this.slides[0].offsetWidth;
    const gap = 16; // 1rem gap
    
    this.slideWidth = slideWidth + gap;
    this.visibleSlides = Math.floor(wrapperWidth / this.slideWidth);
    this.maxIndex = Math.max(0, this.slides.length - this.visibleSlides);
    
    // Reset position if current index is beyond max
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
  }
  
  setupEventListeners() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.previous());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    // Touch/Mouse events for dragging - only on the slider area
    this.slider.addEventListener('mousedown', this.handleStart.bind(this));
    this.slider.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
    
    // Use document for move/end events but check if we're dragging
    document.addEventListener('mousemove', this.handleMove.bind(this));
    document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
    
    document.addEventListener('mouseup', this.handleEnd.bind(this));
    document.addEventListener('touchend', this.handleEnd.bind(this));
    
    // Prevent context menu on long press
    this.slider.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', this.handleKeydown.bind(this));
  }
  
  setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        this.calculateDimensions();
        this.updatePosition();
        this.updateNavigation();
      });
      
      resizeObserver.observe(this.wrapper);
    }
  }
  
  handleStart(e) {
    // Check if this is a touch event
    this.isTouch = e.type.includes('touch');
    
    // Check if the touch/click is within the slider bounds
    const rect = this.slider.getBoundingClientRect();
    const clientX = this.getClientX(e);
    const clientY = this.isTouch ? e.touches[0].clientY : e.clientY;
    
    // Only process if within slider bounds
    if (clientX < rect.left || clientX > rect.right || 
        clientY < rect.top || clientY > rect.bottom) {
      console.log('Touch outside slider bounds, ignoring');
      return;
    }
    
    // Record touch start details for gesture detection
    if (this.isTouch) {
      this.touchStartTime = Date.now();
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.hasMoved = false;
      this.isDragDelayed = false;
      console.log('Touch start detected within slider:', this.touchStartX, this.touchStartY);
      
      // Clear any existing drag delay
      if (this.dragDelay) {
        clearTimeout(this.dragDelay);
        this.dragDelay = null;
      }
      
      // Set up delayed drag detection
      this.dragDelay = setTimeout(() => {
        if (!this.hasMoved) {
          this.isDragDelayed = true;
          console.log('Drag delay activated');
        }
      }, 100); // 100ms delay before enabling drag
      
      // Don't prevent default immediately for touch events
      return;
    }
    
    // For mouse events, start dragging immediately
    this.isDragging = true;
    this.startX = this.getClientX(e);
    this.currentX = this.startX;
    this.translateX = this.currentIndex * -this.slideWidth;
    
    this.track.classList.add('dragging');
    this.slider.classList.add('grabbing');
    
    console.log('Mouse dragging started');
    e.preventDefault();
  }
  
  handleMove(e) {
    // Only process if we're dragging or if this is a touch event we're tracking
    if (!this.isDragging && !this.isTouch) return;
    
    // For touch events, check if we're still within slider bounds
    if (this.isTouch && !this.isWithinSliderBounds(e)) {
      console.log('Touch moved outside slider bounds, stopping tracking');
      // Clean up if we were tracking
      if (this.dragDelay) {
        clearTimeout(this.dragDelay);
        this.dragDelay = null;
      }
      this.isDragging = false;
      this.track.classList.remove('dragging');
      this.slider.classList.remove('grabbing');
      return;
    }
    
    // For touch events, check if we should start dragging
    if (this.isTouch && !this.isDragging) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - this.touchStartX);
      const deltaY = Math.abs(currentY - this.touchStartY);
      
      // If we've moved significantly, start dragging
      if (deltaX > 10 || deltaY > 10) {
        this.hasMoved = true;
        console.log('Touch moved significantly, starting drag:', deltaX, deltaY);
        
        // Clear the drag delay
        if (this.dragDelay) {
          clearTimeout(this.dragDelay);
          this.dragDelay = null;
        }
        
        // Start dragging
        this.isDragging = true;
        this.startX = this.getClientX(e);
        this.currentX = this.startX;
        this.translateX = this.currentIndex * -this.slideWidth;
        
        this.track.classList.add('dragging');
        this.slider.classList.add('grabbing');
        
        // Only prevent default for horizontal swipes
        if (deltaX > deltaY && deltaX > 10) {
          e.preventDefault();
        }
      } else {
        // Not enough movement yet, don't prevent default (allows clicks and page scroll)
        return;
      }
    }
    
    if (!this.isDragging) return;
    
    // For touch events, check if this is actually a horizontal swipe
    if (this.isTouch) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - this.touchStartX);
      const deltaY = Math.abs(currentY - this.touchStartY);
      
      // Only prevent default for clear horizontal swipes
      if (deltaX > deltaY && deltaX > 15) {
        e.preventDefault();
      } else {
        // Allow vertical scrolling
        return;
      }
    }
    
    this.currentX = this.getClientX(e);
    const deltaX = this.currentX - this.startX;
    const newTranslateX = this.translateX + deltaX;
    
    this.track.style.transform = `translateX(${newTranslateX}px)`;
    
    // Only prevent default for mouse events or confirmed horizontal swipes
    if (!this.isTouch || (this.isTouch && this.hasMoved)) {
      e.preventDefault();
    }
  }
  
  handleEnd(e) {
    // Clear any pending drag delay
    if (this.dragDelay) {
      clearTimeout(this.dragDelay);
      this.dragDelay = null;
    }
    
    // Only process if we were actually tracking this touch/click
    if (!this.isDragging && !this.isTouch) return;
    
    // For touch events, check if this was a tap or swipe
    if (this.isTouch && !this.isDragging) {
      const touchDuration = Date.now() - this.touchStartTime;
      const deltaX = Math.abs(this.currentX - this.touchStartX);
      
      // If it was a quick tap with minimal movement, it's a click
      if (touchDuration < 300 && deltaX < 15 && !this.hasMoved) {
        console.log('Quick tap detected, allowing click');
        // Let the click work normally - don't interfere
        return;
      }
    }
    
    if (!this.isDragging) return;
    
    console.log('Touch end, hasMoved:', this.hasMoved);
    
    this.isDragging = false;
    this.track.classList.remove('dragging');
    this.slider.classList.remove('grabbing');
    
    const deltaX = this.currentX - this.startX;
    const threshold = this.slideWidth * 0.15; // Even more reduced threshold for easier swiping
    
    console.log('DeltaX:', deltaX, 'Threshold:', threshold);
    
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        console.log('Swiping to previous');
        this.previous();
      } else {
        console.log('Swiping to next');
        this.next();
      }
    } else {
      console.log('Resetting position');
      this.updatePosition();
    }
  }
  
  handleKeydown(e) {
    if (e.target.closest('[data-product-slider]') !== this.slider) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        break;
    }
  }
  
  getClientX(e) {
    return e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  }
  
  // Check if touch is within slider bounds
  isWithinSliderBounds(e) {
    const rect = this.slider.getBoundingClientRect();
    const clientX = this.getClientX(e);
    const clientY = this.isTouch ? e.touches[0].clientY : e.clientY;
    
    return clientX >= rect.left && clientX <= rect.right && 
           clientY >= rect.top && clientY <= rect.bottom;
  }
  
  previous() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updatePosition();
      this.updateNavigation();
    }
  }
  
  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updatePosition();
      this.updateNavigation();
    }
  }
  
  updatePosition() {
    const translateX = this.currentIndex * -this.slideWidth;
    this.track.style.transform = `translateX(${translateX}px)`;
  }
  
  updateNavigation() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }
  
  // Public method to go to specific slide
  goToSlide(index) {
    if (index >= 0 && index <= this.maxIndex) {
      this.currentIndex = index;
      this.updatePosition();
      this.updateNavigation();
    }
  }
  
  // Public method to refresh slider (useful after dynamic content changes)
  refresh() {
    this.calculateDimensions();
    this.updatePosition();
    this.updateNavigation();
  }
  
  // Cleanup method
  destroy() {
    if (this.dragDelay) {
      clearTimeout(this.dragDelay);
      this.dragDelay = null;
    }
  }
}

// Initialize all product sliders on the page
function initializeSliders() {
  const sliders = document.querySelectorAll('[data-product-slider]:not([data-initialized])');
  sliders.forEach(slider => {
    new ArticleProductSlider(slider);
    slider.setAttribute('data-initialized', 'true');
  });
}

// Multiple initialization triggers to ensure it works on first load
document.addEventListener('DOMContentLoaded', initializeSliders);
document.addEventListener('load', initializeSliders);

// Fallback initialization after a short delay
setTimeout(initializeSliders, 100);

// Re-initialize sliders after dynamic content loads (for AJAX scenarios)
document.addEventListener('shopify:section:load', function(event) {
  const sliders = event.target.querySelectorAll('[data-product-slider]');
  sliders.forEach(slider => {
    slider.removeAttribute('data-initialized');
    new ArticleProductSlider(slider);
    slider.setAttribute('data-initialized', 'true');
  });
});

// Additional fallback for slow-loading content
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSliders);
} else {
  initializeSliders();
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ArticleProductSlider;
}
